import {
  Button,
  Card,
  Col,
  List,
  Row,
  Typography,
  Image,
  Skeleton,
  Space,
  Tag,
} from "antd";
import { useContext, useEffect } from "react";
import { CompanyContext } from "../contexts/company-context";
import ProCard from "@ant-design/pro-card";
import { PageContainer } from "@ant-design/pro-layout";
import { useToken } from "@ant-design/pro-components";
import { logout } from "../Util/logout";

//import { Button, Result, Image } from "antd";

import { ReactComponent as MyImageDark } from "../images/MyTime_Dark.svg";
import { ReactComponent as MyImageWhite } from "../images/MyTime_White.svg";
import { useAccount, useMsal } from "@azure/msal-react";
import Construction_Hulk from "../images/noCompany.png";
import { DarkModeContext } from "../contexts/theme-context";

interface CorporationSelectProps {
  handleCompanyUpdate: (requireCompanySelect: boolean) => void;
}
const CorporationSelect = (props: CorporationSelectProps) => {
  const { handleCompanyUpdate } = props;
  const { updateSelectedCompany, companyData, isCompanyDataLoading } =
    useContext(CompanyContext);
  const { darkMode } = useContext(DarkModeContext);
  const { token } = useToken();

  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const handleLogout = () => logout(instance, account);

  const handleCompanySelect = (value: number) => {
    localStorage.setItem("companySelect", "false");
    updateSelectedCompany(value);
    handleCompanyUpdate(false);
  };

  // If there is only one corporation
  // then auto select that.
  useEffect(() => {
    if (companyData.length === 1) {
      localStorage.setItem("companySelect", "false");
      handleCompanySelect(companyData[0].corporationId);
      handleCompanyUpdate(false);
    }
  }, [companyData]);

  const { Paragraph } = Typography;

  const pageBackground = darkMode
    ? "radial-gradient(1200px 480px at 0% -10%, rgba(255,85,50,0.22), transparent 56%), radial-gradient(900px 420px at 100% 0%, rgba(5,117,230,0.24), transparent 62%), linear-gradient(165deg, #0f1724 0%, #141f33 100%)"
    : "radial-gradient(1200px 520px at 0% -12%, rgba(255,85,50,0.18), transparent 58%), radial-gradient(900px 420px at 100% -6%, rgba(255,189,89,0.24), transparent 64%), linear-gradient(165deg, #f7f9fd 0%, #eef3fa 100%)";

  const heroPanelBackground = darkMode
    ? "linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.08))"
    : "linear-gradient(180deg, rgba(255,255,255,0.94), rgba(255,255,255,0.88))";

  const heroPanelBorder = darkMode
    ? "1px solid rgba(255,255,255,0.12)"
    : `1px solid ${token.colorBorderSecondary}`;

  const cardBackground = darkMode
    ? "linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.06))"
    : "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(250,251,255,0.9))";

  const CustomEmptyList = () => (
    <div
      style={{
        textAlign: "center",
        padding: "24px",
        borderRadius: 18,
        border: heroPanelBorder,
        background: heroPanelBackground,
      }}
    >
      <Image
        preview={false}
        width={320}
        style={{ maxWidth: "100%", marginBottom: 12 }}
        src={Construction_Hulk}
      />
      <Paragraph style={{ color: token.colorText, marginBottom: 6 }}>
        Looks like you do not belong to any corporation yet.
      </Paragraph>
      <Paragraph style={{ color: token.colorTextSecondary, marginBottom: 18 }}>
        Contact your administrator or sign out and try another account.
      </Paragraph>
      <Button onClick={handleLogout} type="default" size="large">
        Log Out
      </Button>
    </div>
  );

  const LoadingShimmer = () => (
    <div style={{ padding: "8px 0" }}>
      <Skeleton
        active
        title={{ width: "38%" }}
        paragraph={{ rows: 2, width: ["100%", "65%"] }}
      />
      <Skeleton
        active
        title={{ width: "62%" }}
        paragraph={{ rows: 2, width: ["100%", "52%"] }}
      />
      <Skeleton
        active
        title={{ width: "48%" }}
        paragraph={{ rows: 2, width: ["100%", "58%"] }}
      />
    </div>
  );

  return (
    <PageContainer
      ghost
      style={{
        background: pageBackground,
        padding: "20px 20px 28px",
        margin: 0,
        width: "100%",
        minHeight: "100vh",
      }}
    >
      <ProCard
        bordered={false}
        style={{
          maxWidth: 980,
          margin: "20px auto 0",
          borderRadius: 22,
          background: heroPanelBackground,
          border: heroPanelBorder,
          boxShadow: darkMode
            ? "0 24px 60px rgba(2,6,23,0.42)"
            : "0 20px 52px rgba(15,23,42,0.14)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Row justify="center" align="top">
          <Col xs={24} lg={20}>
            <div
              style={{
                textAlign: "center",
                margin: "8px 0 24px",
              }}
            >
              {darkMode ? (
                <MyImageDark
                  width={318}
                  height={112}
                  style={{ maxWidth: "95%" }}
                />
              ) : (
                <MyImageWhite
                  width={318}
                  height={112}
                  style={{ maxWidth: "95%" }}
                />
              )}
              <Space direction="vertical" size={8} style={{ marginTop: 12 }}>
                <Tag
                  style={{
                    borderRadius: 999,
                    border: darkMode
                      ? "1px solid rgba(255,255,255,0.28)"
                      : "1px solid rgba(15,23,42,0.16)",
                    background: darkMode
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(15,23,42,0.04)",
                    color: token.colorText,
                    padding: "3px 12px",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}
                >
                  Workspace Access
                </Tag>
                <Typography.Title
                  level={2}
                  style={{
                    margin: 0,
                    color: token.colorText,
                    fontFamily: '"Space Grotesk", "Avenir Next", sans-serif',
                    fontWeight: 700,
                  }}
                >
                  Choose your organization
                </Typography.Title>
              </Space>
            </div>

            <Paragraph
              style={{
                textAlign: "center",
                color: token.colorTextSecondary,
                maxWidth: 620,
                margin: "0 auto 18px",
              }}
            >
              {isCompanyDataLoading
                ? "Loading your organizations..."
                : "Select where you want to work today. You can switch later from the header menu."}
            </Paragraph>
            {isCompanyDataLoading ? (
              <LoadingShimmer />
            ) : (
              <List
                grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
                size="small"
                dataSource={companyData}
                renderItem={(item) => (
                  <List.Item
                    style={{
                      textAlign: "left",
                      padding: "8px 0",
                      cursor: "pointer",
                      display: "flex",
                    }}
                    onClick={() => handleCompanySelect(item.corporationId)}
                  >
                    <Card
                      hoverable
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: 16,
                        border: heroPanelBorder,
                        background: cardBackground,
                        minHeight: 160,
                      }}
                    >
                      <Space
                        direction="vertical"
                        size={10}
                        style={{ width: "100%" }}
                      >
                        <Typography.Title
                          level={4}
                          style={{
                            margin: 0,
                            color: token.colorText,
                            fontFamily:
                              '"Space Grotesk", "Avenir Next", sans-serif',
                          }}
                        >
                          {item.name}
                        </Typography.Title>
                        <Typography.Paragraph
                          ellipsis={{ rows: 3 }}
                          style={{
                            color: token.colorTextSecondary,
                            margin: 0,
                            minHeight: 66,
                          }}
                        >
                          {item.description || "No description provided."}
                        </Typography.Paragraph>
                        <Button type="primary" style={{ width: "100%" }}>
                          Continue
                        </Button>
                      </Space>
                    </Card>
                  </List.Item>
                )}
                locale={{ emptyText: <CustomEmptyList /> }}
              />
            )}
          </Col>
        </Row>
      </ProCard>
    </PageContainer>
  );
};

export default CorporationSelect;
